import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, Trash2, UserPlus, Loader2, MailCheck, MailWarning, ShieldCheck, Clock, Ban, Send, Copy } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
}

const INPUT = "w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none px-3 py-2 text-sm";
const SMALL = "text-xs border px-2 py-1 whitespace-nowrap transition-colors disabled:opacity-40";

const FILTERS = [
  { k: "all", label: "Todos" },
  { k: "pending", label: "Por aprovar" },
  { k: "unverified", label: "E-mail por confirmar" },
  { k: "disabled", label: "Desativados" },
];

function matchFilter(u, f) {
  if (u.role === "admin") return f === "all";
  if (f === "pending") return u.email_verified && !u.approved && !u.disabled;
  if (f === "unverified") return !u.email_verified && !u.disabled;
  if (f === "disabled") return u.disabled;
  return true;
}

function accountStatus(u) {
  if (u.role === "admin") return { label: "admin", cls: "border-[#c8aa6e] text-[#c8aa6e]", Icon: ShieldCheck };
  if (u.disabled) return { label: "desativado", cls: "border-[#ef4444]/60 text-[#ef4444]", Icon: Ban };
  if (!u.email_verified) return { label: u.must_set_password ? "convite enviado" : "e-mail por confirmar", cls: "border-[#f59e0b]/60 text-[#f59e0b]", Icon: MailWarning };
  if (!u.approved) return { label: "por aprovar", cls: "border-[#c8aa6e]/60 text-[#c8aa6e]", Icon: Clock };
  return { label: "aprovado", cls: "border-[#10b981]/60 text-[#10b981]", Icon: MailCheck };
}

function DevLinkToast({ link }) {
  if (!link) return null;
  return (
    <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copiado"); }}
      className="mt-1 text-xs text-[#c8aa6e] underline inline-flex items-center gap-1">
      <Copy className="w-3 h-3" /> Copiar link (modo simulação)
    </button>
  );
}

function notifyLink(title, link) {
  if (link) toast.success(title, { description: <div>E-mail em modo simulação. <DevLinkToast link={link} /></div>, duration: 12000 });
  else toast.success(title);
}

function CreateUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", grant_days: 30, send_invite: true });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", {
        email: form.email.trim(), name: form.name.trim() || "Utilizador",
        grant_days: Number(form.grant_days) || null, send_invite: form.send_invite,
      });
      setResult(data);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar utilizador");
    } finally { setBusy(false); }
  };

  const close = () => { setResult(null); setForm({ email: "", name: "", grant_days: 30, send_invite: true }); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#18181b] border border-[#3f3f46] rounded-none text-[#fafafa] max-w-md" data-testid="create-user-dialog">
        <DialogHeader><DialogTitle className="font-head font-normal text-lg">Criar utilizador</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="create-user-result">
            <p className="text-sm text-[#a1a1aa]">Conta criada para <span className="text-[#fafafa]">{result.user.email}</span>. O utilizador recebe um convite para definir a palavra-passe.</p>
            {result.dev_link && (
              <div className="border border-[#c8aa6e]/40 bg-[#c8aa6e]/5 px-3 py-2 text-xs text-[#a1a1aa]">
                <div className="label-tiny text-[#c8aa6e] mb-1">Modo simulação de e-mail — link de convite</div>
                <a href={result.dev_link} target="_blank" rel="noreferrer" className="text-[#c8aa6e] break-all hover:underline" data-testid="create-user-dev-link">{result.dev_link}</a>
              </div>
            )}
            <button onClick={close} data-testid="create-user-close" className="w-full bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-4 py-2 text-sm font-medium">Fechar</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div><label className="label-tiny block mb-1">Email</label>
              <input data-testid="create-user-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={INPUT} placeholder="cliente@empresa.pt" /></div>
            <div><label className="label-tiny block mb-1">Nome</label>
              <input data-testid="create-user-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={INPUT} placeholder="Nome do cliente" /></div>
            <div><label className="label-tiny block mb-1">Dias de acesso</label>
              <input data-testid="create-user-days" type="number" min={0} value={form.grant_days} onChange={(e) => set("grant_days", e.target.value)} className={INPUT} /></div>
            <label className="flex items-center gap-2 text-sm text-[#a1a1aa]">
              <input type="checkbox" checked={form.send_invite} onChange={(e) => set("send_invite", e.target.checked)} data-testid="create-user-invite" className="accent-[#c8aa6e]" />
              Enviar convite para definir palavra-passe
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={close} className="px-4 py-2 text-sm border border-[#3f3f46] text-[#a1a1aa] hover:text-[#fafafa]">Cancelar</button>
              <button type="submit" disabled={busy} data-testid="create-user-submit" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#c8aa6e] text-black hover:bg-[#d4b982] disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 spin" /> : <UserPlus className="w-4 h-4" />} Criar
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function UsersView() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/users", { params: { search } }).then(({ data }) => setUsers(data));
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const patch = async (u, body, okMsg) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, body);
      notifyLink(okMsg, data.dev_link);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };
  const del = async (u) => {
    if (!window.confirm(`Eliminar ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Utilizador eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const visible = users.filter((u) => matchFilter(u, filter));
  const pendingCount = users.filter((u) => matchFilter(u, "pending")).length;

  return (
    <div className="space-y-4">
      {pendingCount > 0 && (
        <div data-testid="pending-banner" className="border border-[#c8aa6e]/40 bg-[#c8aa6e]/5 px-4 py-3 text-sm text-[#e4e4e7] flex items-center gap-3">
          <Clock className="w-4 h-4 text-[#c8aa6e]" />
          <span><span className="text-[#c8aa6e] font-medium">{pendingCount}</span> conta{pendingCount > 1 ? "s" : ""} com e-mail confirmado à espera da sua aprovação.</span>
          <button onClick={() => setFilter("pending")} className="ml-auto text-xs text-[#c8aa6e] underline" data-testid="pending-banner-view">Ver</button>
        </div>
      )}
      <div className="bg-[#18181b] border border-[#27272a]" data-testid="admin-users-table">
        <div className="p-4 border-b border-[#27272a] flex flex-wrap items-center gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar email…" data-testid="users-search"
            className="w-full max-w-xs bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none px-3 py-2 text-sm" />
          <div className="flex border border-[#27272a]">
            {FILTERS.map((f) => (
              <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`users-filter-${f.k}`}
                className={`px-3 py-2 text-xs whitespace-nowrap ${filter === f.k ? "bg-[#c8aa6e] text-black" : "text-[#a1a1aa] hover:text-[#fafafa]"}`}>
                {f.label}{f.k === "pending" && pendingCount > 0 && <span className="ml-1 opacity-70">{pendingCount}</span>}
              </button>
            ))}
          </div>
          <button onClick={() => setCreateOpen(true)} data-testid="create-user-btn"
            className="ml-auto inline-flex items-center gap-1.5 bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-3 py-2 text-sm font-medium">
            <UserPlus className="w-4 h-4" /> Criar utilizador
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="label-tiny">
                <th className="px-4 py-3">Email</th><th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Conta</th><th className="px-4 py-3">Plano</th>
                <th className="px-4 py-3">Acesso</th><th className="px-4 py-3">Expira</th>
                <th className="px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u, i) => {
                const st = accountStatus(u);
                const isAdmin = u.role === "admin";
                return (
                  <tr key={u.id} data-testid={`admin-user-${i}`} className="border-t border-[#27272a] hover:bg-[#27272a]/30">
                    <td className="px-4 py-2 text-[#fafafa]">{u.email}</td>
                    <td className="px-4 py-2 text-[#a1a1aa]">{u.name}</td>
                    <td className="px-4 py-2">
                      <span data-testid={`user-status-${i}`} className={`text-xs px-2 py-0.5 border inline-flex items-center gap-1 ${st.cls}`}><st.Icon className="w-3 h-3" />{st.label}</span>
                    </td>
                    <td className="px-4 py-2 text-[#a1a1aa]">{u.plan || (u.trial ? "trial" : "—")}</td>
                    <td className="px-4 py-2">
                      {u.is_active ? <span className="text-[#10b981] inline-flex items-center gap-1 text-xs"><Check className="w-3 h-3" />ativo</span>
                        : <span className="text-[#ef4444] inline-flex items-center gap-1 text-xs"><X className="w-3 h-3" />inativo</span>}
                    </td>
                    <td className="px-4 py-2 text-[#a1a1aa] whitespace-nowrap">{fmtDate(u.access_expires_at)}</td>
                    <td className="px-4 py-2">
                      {!isAdmin && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {!u.approved && !u.disabled && (
                            <button onClick={() => patch(u, { approved: true, email_verified: true }, `Conta aprovada: ${u.email}`)} data-testid={`approve-${i}`}
                              className={`${SMALL} border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10`}>Aprovar</button>
                          )}
                          {!u.email_verified && !u.disabled && (
                            <button onClick={() => patch(u, u.must_set_password ? { resend_invite: true } : { resend_verification: true }, "E-mail reenviado")} data-testid={`resend-${i}`}
                              title={u.must_set_password ? "Reenviar convite" : "Reenviar confirmação de e-mail"}
                              className={`${SMALL} border-[#3f3f46] text-[#a1a1aa] hover:border-[#c8aa6e] inline-flex items-center gap-1`}><Send className="w-3 h-3" />reenviar</button>
                          )}
                          <button onClick={() => patch(u, { grant_days: 30 }, `Acesso concedido (30 dias) a ${u.email}`)} title="Conceder 30 dias" data-testid={`grant-${i}`}
                            className={`${SMALL} border-[#3f3f46] text-[#a1a1aa] hover:border-[#c8aa6e]`}>+30d</button>
                          <button onClick={() => patch(u, { revoke: true }, `Acesso revogado a ${u.email}`)} title="Revogar acesso" data-testid={`revoke-${i}`}
                            className={`${SMALL} border-[#3f3f46] text-[#a1a1aa] hover:border-[#ef4444]`}>revogar</button>
                          {u.disabled ? (
                            <button onClick={() => patch(u, { disabled: false }, `Conta reativada: ${u.email}`)} data-testid={`enable-${i}`}
                              className={`${SMALL} border-[#10b981]/60 text-[#10b981] hover:bg-[#10b981]/10`}>reativar</button>
                          ) : (
                            <button onClick={() => patch(u, { disabled: true }, `Conta desativada: ${u.email}`)} data-testid={`disable-${i}`}
                              className={`${SMALL} border-[#3f3f46] text-[#a1a1aa] hover:border-[#ef4444] hover:text-[#ef4444]`}>desativar</button>
                          )}
                          <button onClick={() => del(u)} data-testid={`delete-${i}`} className="text-[#71717a] hover:text-[#ef4444] ml-1" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#71717a] text-sm">Sem utilizadores neste filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
    </div>
  );
}
